import { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import { brand, text } from '../../theme/colors';

/**
 * "Request a viewing" — an enquiry that says when the buyer could actually walk
 * the property. Dates are free text on purpose: a buyer in Birmingham thinks in
 * "the week of the 12th", not in a date picker, and the agent reads it anyway.
 */
export default function ViewingDialog({ open, sending = false, error = '', onClose, onSubmit }) {
  const [preferredDates, setPreferredDates] = useState('');
  const [message, setMessage] = useState('');

  // A dialog reopened after a refusal should not still hold the last attempt's
  // text if the buyer closed it in between.
  useEffect(() => {
    if (!open) { setPreferredDates(''); setMessage(''); }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={() => (sending ? null : onClose && onClose())}
      maxWidth="sm"
      fullWidth
      aria-labelledby="listing-viewing-title"
    >
      <DialogTitle id="listing-viewing-title">Request a viewing</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: text.secondary, mb: 2 }}>
          We will ask the agent to arrange a viewing and open a message thread with them, so what you agree is
          written down. Tell them when you could go — or when someone could go on your behalf.
        </Typography>
        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Preferred dates"
            helperText="e.g. next week, weekday mornings"
            inputProps={{ maxLength: 200 }}
            value={preferredDates}
            onChange={(e) => setPreferredDates(e.target.value)}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Your message (optional)"
            inputProps={{ maxLength: 2000 }}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose && onClose()} disabled={sending}>Cancel</Button>
        <Button
          variant="contained"
          disabled={sending}
          onClick={() => onSubmit && onSubmit({ preferredDates: preferredDates.trim(), message: message.trim() })}
          sx={{ background: brand.gradient, '&:hover': { background: brand.gradientHover } }}
        >
          {sending ? 'Sending…' : 'Request viewing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
